<script setup lang="ts">
import { mdiHeart, mdiHeartOutline } from "@mdi/js";
import NavIcon from "../../Nav/NavIcon.vue";
import { useAuth } from "~/store/auth";
import { useBetbook } from "~/store/betbook";
import { ISport } from "~/types/sports/sport";
import sportUrlBuilder from "~/utils/sport/sportUrlBuilder";
import { useSettings } from "~/store/settings";

interface Props {
	league: ISport["topLeagues"][0];
}

defineProps<Props>();
const { $trackEvent } = useNuxtApp();
const settings = useSettings();
const route = useRoute();
const router = useRouter();
const auth = useAuth();
const store = useBetbook();

const handleFav = (id: string, region: string, sportId: string, title: string) => {
	if (!auth.loggedIn) {
		if (settings.isNativeApp) return window.promptLogin();
		auth.modals.login = true;
	} else if (store.isFavourite(id, "League")) {
		store.removeFavourite(id, "League");
	} else {
		store.addFavourite({ id, region, sportId, title, favouriteType: "League" });
	}
};

const handleClick = (league: ISport["topLeagues"][0]) => {
	store.filters.regionAndLeagueIds = [{ regionId: league.regionId, leagueId: league.leagueId }];
	store.applyingFilters = true;
	$trackEvent.topLeagueSelected(league.friendlyName || league.defaultName);
	router
		.push({
			path: sportUrlBuilder({ fixtureType: "highlights", sport: league.sportId }),
			query: {
				...route.query,
				selectedLeagues: `${league.regionId}_${league.leagueId}`,
			},
		})
		.then(() => (store.applyingFilters = false));
};
</script>

<template>
	<div
		@click="handleClick(league)"
		:key="league.id"
		class="flex items-center gap-2 p-1 rounded cursor-pointer bg-light-50 dark:bg-dark-700 shrink-0"
		:class="{ 'px-2': !auth.loggedIn }"
	>
		<div
			v-if="auth.loggedIn && settings.hasFeature('IsLeagueFavouriteEnabled')"
			@click="
				(e) => {
					e.stopPropagation();
					handleFav(`${league.regionId}_${league.leagueId}`, league.regionId, league.sportId, league.defaultName);
				}
			"
			class="flex items-center justify-center w-8 h-8 rounded-sm cursor-pointer bg-light-200 dark:bg-dark-800"
		>
			<UiIcon
				:path="store.isFavourite(`${league.regionId}_${league.leagueId}`, 'League') ? mdiHeart : mdiHeartOutline"
				:class="[
					'w-6 h-6',
					store.isFavourite(`${league.regionId}_${league.leagueId}`, 'League') ? 'fill-identity' : 'fill-dark-800 dark:fill-light-600',
				]"
			/>
		</div>
		<div class="flex flex-col text-xs leading-4 whitespace-nowrap">
			<strong>{{ league.friendlyName || league.defaultName }}</strong>
			<div class="flex items-center gap-1 capitalize text-dark-500 dark:text-light-600 shrink-0">
				<DataFreeGuard>
					<NavIcon
						class="flex shrink-0"
						:width="16"
						:height="16"
						:src="`/icons/regions-leagues-flags/${league.regionId.toLowerCase()}-circular.svg`"
						:alt="league.defaultName"
						fallback="/icons/regions-leagues-flags/default-circular.svg"
					/>
				</DataFreeGuard>
				{{ league.regionId.replace(/-/g, " ") }}
			</div>
		</div>
	</div>
</template>
